import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  YMaps,
  Map,
  Polyline,
  Placemark,
  ZoomControl,
  TypeSelector,
} from '@pbe/react-yandex-maps';
import dayjs from 'dayjs';

const getSiteColor = (site) => {
  if (site.has_unemptied_containers) return "#e55353"; // Красный: невыгруженные баки
  if (site.has_overflow || site.has_bulky_waste || site.has_container_defect) return "#e65100"; // Оранжевый: навал / КГО / дефект
  return "#2eb85c"; // Зеленый: без нарушений
};

const GPSMap = ({ 
  trajectory, 
  currentTime, 
  height = "350px", 
  sites = [], 
  onSiteClick = null 
}) => {
  const mapRef = useRef(null);
  const prevTimeRef = useRef(currentTime);

  const EMPTY_MAP_STYLE = {
    height: height,
    minHeight: '260px',
    width: '100%',
    background: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    border: '1px solid #ddd',
  };

  const MAP_CONTAINER_STYLE = {
    height: height,
    minHeight: '260px',
    width: '100%',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #ccc',
  };

  const coordinates = useMemo(() => {
    if (!trajectory || trajectory.length === 0) return [];
    return trajectory.map((p) => [p.lat, p.lon]);
  }, [trajectory]);

  const validSites = useMemo(() => {
    if (!sites || sites.length === 0) return [];
    return sites.filter((s) => s.latitude > 0 && s.longitude > 0);
  }, [sites]);

  const currentPosition = useMemo(() => {
    if (!trajectory || trajectory.length === 0 || !currentTime) return null;

    const targetTs = dayjs(currentTime).valueOf();

    if (trajectory.length < 2) {
      return [trajectory[0].lat, trajectory[0].lon];
    }

    const firstTs = dayjs(trajectory[0].ts).valueOf();
    const lastTs = dayjs(trajectory[trajectory.length - 1].ts).valueOf();

    if (targetTs <= firstTs) return [trajectory[0].lat, trajectory[0].lon];
    if (targetTs >= lastTs) {
      const lastPoint = trajectory[trajectory.length - 1];
      return [lastPoint.lat, lastPoint.lon];
    }

    let prevIndex = -1;
    for (let i = 0; i < trajectory.length - 1; i += 1) {
      const t1 = dayjs(trajectory[i].ts).valueOf();
      const t2 = dayjs(trajectory[i + 1].ts).valueOf();

      if (targetTs >= t1 && targetTs < t2) {
        prevIndex = i;
        break;
      }
    }

    if (prevIndex !== -1) {
      const p1 = trajectory[prevIndex];
      const p2 = trajectory[prevIndex + 1];

      const t1 = dayjs(p1.ts).valueOf();
      const t2 = dayjs(p2.ts).valueOf();

      const factor = (targetTs - t1) / (t2 - t1);

      const lat = p1.lat + (p2.lat - p1.lat) * factor;
      const lon = p1.lon + (p2.lon - p1.lon) * factor;

      return [lat, lon];
    }

    return [trajectory[0].lat, trajectory[0].lon];
  }, [trajectory, currentTime]);

  useEffect(() => {
    if (mapRef.current && currentPosition && currentTime) {
      const prev = prevTimeRef.current;
      
      const isFirstLoad = !prev;
      const isJump = Math.abs(dayjs(currentTime).diff(dayjs(prev), 'second')) > 5;

      if (isFirstLoad || isJump) {
        mapRef.current.setCenter(currentPosition);
      }
      prevTimeRef.current = currentTime;
    }
  }, [currentTime, currentPosition]);

  if (coordinates.length === 0 && validSites.length === 0) {
    return (
      <div style={EMPTY_MAP_STYLE}>
        <span className="text-muted">Нет GPS данных за этот период</span>
      </div>
    );
  }

  const initialCenter = currentPosition 
    || coordinates[0] 
    || (validSites.length > 0 ? [validSites[0].latitude, validSites[0].longitude] : [55.751574, 37.573856]);

  const mapState = {
    center: initialCenter,
    zoom: 14,
    controls: [],
  };

  return (
    <div style={MAP_CONTAINER_STYLE}>
      <YMaps query={{ lang: 'ru_RU' }}>
        <Map
          instanceRef={(ref) => {
            mapRef.current = ref;
          }}
          defaultState={mapState}
          width="100%"
          height="100%"
        >
          {coordinates.length > 1 && (
            <Polyline
              geometry={coordinates}
              options={{
                balloonCloseButton: false,
                strokeColor: '#0000FF',
                strokeWidth: 4,
                strokeOpacity: 0.7,
              }}
            />
          )}

          {/* Отображение точек остановок КП */}
          {validSites.map((site, index) => {
            const hasViolations = site.has_unemptied_containers || site.has_overflow || site.has_bulky_waste || site.has_container_defect;
            const timeRangeStr = `${site.start ? dayjs(site.start).format("HH:mm") : ""} - ${site.end ? dayjs(site.end).format("HH:mm") : ""}`;

            return (
              <Placemark
                key={site.id || index}
                geometry={[site.latitude, site.longitude]}
                properties={{
                  hintContent: `<strong>КП #${index + 1}</strong> (${timeRangeStr})<br/>Контейнеров: ${site.containers_count || 0}<br/>Кликните для перемотки видео`,
                  balloonContent: `
                    <div style="font-size: 13px;">
                      <strong>КП #${index + 1}</strong><br/>
                      ${site.address ? `<span>${site.address}</span><br/>` : ""}
                      Время: ${timeRangeStr}<br/>
                      Контейнеров: <strong>${site.containers_count || 0} шт.</strong><br/>
                      ${hasViolations ? `<span style="color: #e55353; font-weight: bold;">Есть замечания ИИ</span><br/>` : `<span style="color: #2eb85c; font-weight: bold;">Без нарушений</span><br/>`}
                      <small style="color: #0d6efd; text-decoration: underline; cursor: pointer;">Кликните для просмотра видео</small>
                    </div>
                  `
                }}
                options={{
                  preset: 'islands#circleDotIcon',
                  iconColor: getSiteColor(site),
                }}
                modules={["geoObject.addon.hint", "geoObject.addon.balloon"]}
                onClick={() => {
                  if (onSiteClick) onSiteClick(site);
                }}
              />
            );
          })}

          {currentPosition && (
            <Placemark
              geometry={currentPosition}
              options={{
                preset: 'islands#blueAutoIcon',
                iconColor: 'red',
                zIndex: 1000,
              }}
              properties={{
                balloonContent: `Время: ${dayjs(currentTime).format('HH:mm:ss')}`,
              }}
            />
          )}

          <ZoomControl options={{ float: 'right' }} />
          <TypeSelector options={{ float: 'right' }} />
        </Map>
      </YMaps>
    </div>
  );
};

GPSMap.propTypes = {
  trajectory: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lon: PropTypes.number.isRequired,
      ts: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    })
  ),
  currentTime: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  height: PropTypes.string,
  sites: PropTypes.array,
  onSiteClick: PropTypes.func,
};

GPSMap.defaultProps = {
  trajectory: [],
  currentTime: null,
  height: "350px",
  sites: [],
  onSiteClick: null,
};

export default GPSMap;